import { useTranslation } from 'i18n';
import { useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, LinearProgress, Stack, Typography, useBreakpoint } from 'ui';

import { formatAiModel } from '../../../breakdowns';
import {
  AiAluModels,
  AiHdpeModels,
  AiPetModels,
  isPetBaledAiModel,
  usePresentationPreference,
} from '../../../evidence';
import { AiBaleGuide } from '../AiBaleGuide/AiBaleGuide';
import { AiBriquetteGuide } from '../AiBriquetteGuide/AiBriquetteGuide';
import { AiLooseGuide } from '../AiLooseGuide/AiLooseGuide';

type HelpGuideModels = AiPetModels | AiAluModels | AiHdpeModels;

const maxSteps: Record<HelpGuideModels, number> = {
  alu: 3,
  alu_loose: 4,
  briquette: 3,
  hdpe: 3,
  pet: 3,
  pet_dummy_bale: 3,
  pet_dummy_bale_2: 3,
  pet_envases: 3,
  pet_loose: 4,
};

export const HelpGuideDialog = ({
  open,
  onClose,
  aiModel,
  onComplete: onOpenCamera,
}: {
  open: boolean;
  onClose: () => void;
  aiModel: HelpGuideModels;
  onComplete: () => void;
}) => {
  const { obfuscateAi } = usePresentationPreference();
  const { t } = useTranslation('features');

  const modelTitle = formatAiModel(aiModel, obfuscateAi);
  const [currentStep, setCurrentStep] = useState(1);

  const isLastStep = currentStep === maxSteps[aiModel];

  const maxProgress = 100 / maxSteps[aiModel];

  const handleClose = () => {
    onClose();
    setCurrentStep(1);
  };

  const nextStep = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const prevStep = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const handleOpenCamera = () => {
    onOpenCamera();
    handleClose();
  };

  const { isMobile } = useBreakpoint();

  return (
    <Dialog open={open} fullScreen={isMobile}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
          }}
        >
          {t('helpGuides.dialog.title', { values: { modelTitle } })}
        </DialogTitle>

        <DialogContent>
          <Stack
            sx={{
              gap: 2,
            }}
          >
            {aiModel === 'alu_loose' ? <AiLooseGuide currentStep={currentStep} obfuscateAi={obfuscateAi} /> : null}
            {aiModel === 'pet_loose' ? <AiLooseGuide currentStep={currentStep} obfuscateAi={obfuscateAi} /> : null}
            {aiModel === 'alu' || isPetBaledAiModel(aiModel) || aiModel === 'hdpe' ? (
              <AiBaleGuide currentStep={currentStep} obfuscateAi={obfuscateAi} />
            ) : null}
            {aiModel === 'briquette' ? <AiBriquetteGuide currentStep={currentStep} obfuscateAi={obfuscateAi} /> : null}
          </Stack>
        </DialogContent>

        <Stack
          sx={{
            bgcolor: 'background.paper',
            padding: 2,
            textAlign: 'center',
            gap: 2,
            position: 'sticky',
            bottom: 0,
            zIndex: 299,
          }}
        >
          <Box>
            <LinearProgress showPercentage={false} value={currentStep * maxProgress} />
            <Typography color="secondary" variant="body2">
              {t('helpGuides.dialog.stepLabel', { values: { currentStep, maxSteps: maxSteps[aiModel] } })}
            </Typography>
          </Box>

          <Stack
            sx={{
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              {currentStep !== 1 ? (
                <Button variant="secondary" fullWidth onClick={prevStep}>
                  {t('helpGuides.common.actions.back')}
                </Button>
              ) : null}
              {isLastStep ? (
                <Button fullWidth onClick={handleOpenCamera}>
                  {t('helpGuides.common.actions.finish')}
                </Button>
              ) : (
                <Button fullWidth onClick={nextStep}>
                  {t('helpGuides.common.actions.next')}
                </Button>
              )}
            </Box>
            <Button variant="text" onClick={handleClose} data-testid="skip-guide">
              {t('helpGuides.common.actions.skipGuide')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
};
